import { useReducer } from 'react'
import { config } from '~app/config'
import { ScanCenterCode } from '~app/graph/generated/graphql'

interface FilterState {
  customerId: string
  excludeCustomerId: boolean
  scanCenter: ScanCenterCode
  excludeScanCenter: boolean
  batchSize: number
  isFiltered: boolean
  isFetching: boolean
}

interface FilterStateAction {
  type: string
  payload?: number | boolean | string
}

const initialState: FilterState = {
  // by default, exclude customer id for N26
  customerId: config.N26CustomerId || '',
  excludeCustomerId: true,
  // by default, exclude SRZ
  scanCenter: ScanCenterCode.Srz,
  excludeScanCenter: true,
  batchSize: 10,
  isFiltered: true,
  isFetching: false,
}

const reducer = (
  state: FilterState,
  action: FilterStateAction
): FilterState => {
  const { type, payload } = action

  switch (type) {
    case 'SET_CUSTOMER_ID':
      return {
        ...state,
        customerId: payload as string,
        isFiltered: true,
      }
    case 'SET_EXCLUDE_CUSTOMER_ID':
      return {
        ...state,
        excludeCustomerId: payload as boolean,
        isFiltered: true,
      }
    case 'SET_SCAN_CENTER':
      return {
        ...state,
        scanCenter: payload as ScanCenterCode,
        isFiltered: true,
      }
    case 'SET_EXCLUDE_SCAN_CENTER':
      return {
        ...state,
        excludeScanCenter: payload as boolean,
        isFiltered: true,
      }
    case 'SET_BATCH_SIZE':
      return {
        ...state,
        batchSize: payload as number,
        isFiltered: true,
      }
    case 'SET_IS_FETCHING':
      return {
        ...state,
        isFetching: payload as boolean,
      }
    case 'SET_IS_FILTERED':
      return {
        ...state,
        isFiltered: payload as boolean,
      }
    case 'RESET_FILTER':
    default:
      return initialState
  }
}

export type UseInboxFilterReducer = [
  FilterState,
  {
    setCustomerId: (customerId: string) => void
    setExcludeCustomerId: (excludeCustomerId: boolean) => void
    setScanCenter: (scanCenter: ScanCenterCode) => void
    setExcludeScanCenter: (excludeScanCenter: boolean) => void
    setBatchSize: (batchSize: number) => void
    setIsFetching: (isFetching: boolean) => void
    setIsFiltered: (isFiltered: boolean) => void
    resetFilter: () => void
  },
]

export const useInboxFilterReducer = (): UseInboxFilterReducer => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setCustomerId = (customerId: string) =>
    dispatch({ type: 'SET_CUSTOMER_ID', payload: customerId })
  const setExcludeCustomerId = (excludeCustomerId: boolean) =>
    dispatch({ type: 'SET_EXCLUDE_CUSTOMER_ID', payload: excludeCustomerId })
  const setScanCenter = (scanCenter: ScanCenterCode) =>
    dispatch({ type: 'SET_SCAN_CENTER', payload: scanCenter })
  const setExcludeScanCenter = (excludeScanCenter: boolean) =>
    dispatch({ type: 'SET_EXCLUDE_SCAN_CENTER', payload: excludeScanCenter })
  const setBatchSize = (batchSize: number) =>
    dispatch({ type: 'SET_BATCH_SIZE', payload: batchSize })
  const setIsFetching = (isFetching: boolean) =>
    dispatch({ type: 'SET_IS_FETCHING', payload: isFetching })
  const setIsFiltered = (isFiltered: boolean) =>
    dispatch({ type: 'SET_IS_FILTERED', payload: isFiltered })
  const resetFilter = () => dispatch({ type: 'RESET_FILTER' })

  return [
    state,
    {
      setCustomerId,
      setExcludeCustomerId,
      setScanCenter,
      setExcludeScanCenter,
      setBatchSize,
      setIsFetching,
      setIsFiltered,
      resetFilter,
    },
  ]
}
